import {
  type AccountSession,
  AccountSessionSchema,
  type AuthResponse,
  AuthResponseSchema,
  type CharacterSummary,
  CharacterSummarySchema,
  type ClassId,
  type Look,
  type Roster,
  RosterSchema,
} from '@shefira/shared';
import { z } from 'zod';

import { AuthError, refusalError, saveSession, serverHttpUrl } from './session.js';

/**
 * The account endpoints, and the account session that outlives a reload.
 *
 * Two tokens live side by side: the account one lists and creates characters,
 * the character one joins zones. Both are just tokens in localStorage
 * (CLAUDE.md), never game state.
 */

const ACCOUNT_KEY = 'shefira.account';

/** One call to the HTTP API, the bearer token and the JSON error line handled once. */
export async function call<T>(
  path: string,
  schema: z.ZodType<T>,
  init: { method: string; token?: string; body?: unknown; headers?: Record<string, string> },
): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${serverHttpUrl()}${path}`, {
      method: init.method,
      headers: {
        ...(init.body === undefined ? {} : { 'content-type': 'application/json' }),
        ...(init.token === undefined ? {} : { authorization: `Bearer ${init.token}` }),
        ...init.headers,
      },
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
    });
  } catch {
    throw new AuthError('Serveur injoignable. Il tourne bien ?', 'unreachable');
  }
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    throw refusalError(body, 'Action refusée', response.status);
  }
  const parsed = schema.safeParse(body);
  if (!parsed.success) throw new AuthError('Réponse du serveur inattendue', 'bad_response');
  return parsed.data;
}

export function loadAccount(): AccountSession | null {
  try {
    const raw = localStorage.getItem(ACCOUNT_KEY);
    if (raw === null) return null;
    const parsed = AccountSessionSchema.safeParse(JSON.parse(raw));
    if (!parsed.success || parsed.data.expiresAt <= Date.now()) {
      localStorage.removeItem(ACCOUNT_KEY);
      return null;
    }
    return parsed.data;
  } catch {
    return null;
  }
}

export function saveAccount(session: AccountSession): void {
  try {
    localStorage.setItem(ACCOUNT_KEY, JSON.stringify(session));
  } catch {
    /* a private window can refuse storage; the session still works for this tab */
  }
}

export function clearAccount(): void {
  try {
    localStorage.removeItem(ACCOUNT_KEY);
  } catch {
    /* nothing to clean up */
  }
}

export async function login(email: string, password: string): Promise<AccountSession> {
  const session = await call('/auth/login', AccountSessionSchema, {
    method: 'POST',
    body: { email, password },
  });
  saveAccount(session);
  return session;
}

/** Ask for a reset link (ADR 0056); the server answers the same whether the address is known or not. */
export async function requestPasswordReset(email: string): Promise<void> {
  await call('/auth/password/forgot', z.object({ sent: z.literal(true) }), {
    method: 'POST',
    body: { email },
  });
}

/** Set a new password from a mailed link; it signs in, like a login. */
export async function resetPassword(token: string, password: string): Promise<AccountSession> {
  const session = await call('/auth/password/reset', AccountSessionSchema, {
    method: 'POST',
    body: { token, password },
  });
  saveAccount(session);
  return session;
}

/**
 * The token of a reset link the page was opened from, if any, wiped from the
 * address bar at once so a reload or a shared screenshot never carries it.
 */
export function consumeResetLink(): string | null {
  const query = new URLSearchParams(window.location.search);
  const token = query.get('reset');
  if (token === null) return null;
  query.delete('reset');
  const rest = query.toString();
  window.history.replaceState(
    null,
    '',
    `${window.location.pathname}${rest === '' ? '' : `?${rest}`}`,
  );
  return token === '' ? null : token;
}

/** Turn the current guest account into a real one; the character token proves it is ours. */
export async function upgradeAccount(
  characterToken: string,
  email: string,
  password: string,
): Promise<string> {
  const result = await call('/auth/upgrade', z.object({ email: z.string() }), {
    method: 'POST',
    token: characterToken,
    body: { email, password },
  });
  return result.email;
}

/** The account's characters and email, from either token kind. */
export async function listRoster(token: string): Promise<Roster> {
  return call('/auth/characters', RosterSchema, { method: 'GET', token });
}

export async function createCharacter(
  token: string,
  nickname: string,
  classId: ClassId,
  look: Look,
): Promise<CharacterSummary> {
  return call('/auth/characters', CharacterSummarySchema, {
    method: 'POST',
    token,
    body: { nickname, classId, look },
  });
}

/** Trade any session for a character session, and store it as the current one. */
export async function selectCharacter(token: string, characterId: string): Promise<AuthResponse> {
  const session = await call('/auth/session', AuthResponseSchema, {
    method: 'POST',
    token,
    body: { characterId },
  });
  saveSession(session);
  return session;
}

/** Delete the whole account (ADR 0075); refused while one of its characters is in the game. */
export async function deleteAccount(token: string): Promise<void> {
  await call('/auth/account', z.object({ deleted: z.literal(true) }), {
    method: 'DELETE',
    token,
    body: { confirm: true },
  });
}

/** Delete one of the account's characters; the server wants its name typed again. */
export async function deleteCharacter(
  token: string,
  characterId: string,
  name: string,
): Promise<void> {
  await call(
    `/auth/characters/${encodeURIComponent(characterId)}`,
    z.object({ deleted: z.string() }),
    {
      method: 'DELETE',
      token,
      body: { name },
    },
  );
}
